import { HttpErrorResponse } from '@angular/common/http';

/**
 * Ponto único de tradução de erro em texto (`MSG-04`): componentes, diálogos e services
 * passam por aqui. Antes disso, cada tela lia o corpo do erro do seu jeito — quatro cópias
 * de `mensagemErro()` e uma dúzia de leituras inline, uma delas já divergente.
 *
 * Ordem de leitura:
 *
 * 1. `MSG-02` — o texto que o backend escreveu, quando veio corpo;
 * 2. o texto local da tela (`padrao`), para o caso **sem corpo**, que é o que `MSG-02`
 *    autoriza e o que diz qual operação falhou;
 * 3. `MSG-05` — texto por status, genérico, quando a tela não ofereceu texto nenhum.
 *
 * O `message` do `HttpErrorResponse` **não** entra: ele é a string técnica do Angular
 * ("Http failure response for http://…: 500 OK"), que não explica nada a quem está na tela.
 */
const MENSAGEM_POR_STATUS: Record<number, string> = {
  0: 'Não foi possível falar com o servidor. Verifique se a API está no ar.',
  400: 'Dados inválidos.',
  401: 'Sessão expirada. Entre novamente.',
  403: 'Você não tem permissão para esta operação.',
  404: 'Registro não encontrado.',
  409: 'Operação em conflito com o estado atual do registro.',
  422: 'Dados inválidos.',
  500: 'Erro interno no servidor.',
};

/**
 * `MSG-03` — os campos candidatos são os que o contrato desta API declara. São dois formatos
 * de erro, conforme `docs/specs/convencoes.spec.md` da igreja-api:
 *
 * - `{ erro, status, timestamp }` — RecursoNaoEncontradoException (404), RegraNegocioException
 *   (400) e CdnIntegrationException (502), pelo GlobalExceptionHandler;
 * - `{ timestamp, message, path }` — qualquer outra exceção (500).
 *
 * `erro` vem **primeiro** porque é o campo das recusas que têm explicação acionável. Ele
 * estava fora da leitura até aqui, e o efeito era a tela descartar o texto do backend e
 * mostrar o próprio texto genérico em todo 400 e 404 (`MSG-02`).
 *
 * `mensagem` fica na lista como candidato barato para o caso de o contrato passar a usá-lo.
 */
function textoDoBackend(corpo: unknown): string {
  if (typeof corpo === 'string') {
    return limparJsonDoTexto(corpo);
  }
  return campoDeMensagem(corpo);
}

/**
 * `error_description` é o campo do OAuth2: é por ele que o servidor de autenticação explica a
 * recusa, e ele chega até aqui colado no texto do login (ver `limparJsonDoTexto`). Vem antes
 * de `error`, que no OAuth2 é um código (`invalid_grant`), não uma frase.
 */
function campoDeMensagem(corpo: unknown): string {
  if (corpo && typeof corpo === 'object') {
    const c = corpo as Record<string, unknown>;
    for (const campo of ['erro', 'mensagem', 'message', 'error_description', 'error']) {
      const candidato = c[campo];
      if (typeof candidato === 'string' && candidato.trim()) {
        return candidato.trim();
      }
    }
  }
  return '';
}

/**
 * Quem está na tela nunca lê JSON. Corpo de texto pode trazer JSON de dois jeitos:
 *
 * - **inteiro**, quando o servidor respondeu JSON com `Content-Type` de texto;
 * - **colado no fim de uma frase**, que é o que o login faz hoje: "Falha na autenticação:
 *   HTTP 400 — {"error":"invalid_grant",…}", com o corpo cru do servidor de autenticação
 *   (`PEND-12`).
 *
 * Nos dois casos o JSON dá lugar ao campo de mensagem que ele carrega; sem campo, sai inteiro.
 */
function limparJsonDoTexto(texto: string): string {
  const t = texto.trim();
  const inicio = t.search(/[{[]/);
  if (inicio < 0) {
    return t;
  }
  let objeto: unknown;
  try {
    objeto = JSON.parse(t.slice(inicio));
  } catch {
    return t;
  }
  const doJson = campoDeMensagem(objeto);
  const antes = t.slice(0, inicio).replace(/[\s:—–-]+$/, '').trim();
  if (!antes) {
    return doJson;
  }
  return doJson ? `${antes}: ${doJson}` : antes;
}

export function mensagemDeErro(erro: unknown, padrao?: string): string {
  if (typeof erro === 'string' && erro.trim()) {
    return erro.trim();
  }
  if (erro instanceof HttpErrorResponse) {
    return textoDoBackend(erro.error) || padrao || MENSAGEM_POR_STATUS[erro.status] ||
      `Falha na requisição (HTTP ${erro.status}).`;
  }
  // Erro que não veio do HttpClient (ex.: falha de API do navegador).
  const e = erro as { error?: unknown; status?: unknown } | null;
  const doCorpo = textoDoBackend(e?.error);
  if (doCorpo) {
    return doCorpo;
  }
  if (padrao) {
    return padrao;
  }
  if (typeof e?.status === 'number' && MENSAGEM_POR_STATUS[e.status]) {
    return MENSAGEM_POR_STATUS[e.status];
  }
  return 'Falha inesperada.';
}

/**
 * Título que o contrato traz junto do erro, quando traz — em tela que dedica espaço ao erro
 * ele é metade da explicação (`MSG-03`). Devolve string vazia quando não há.
 */
export function tituloDeErro(erro: unknown): string {
  const corpo = erro instanceof HttpErrorResponse ? erro.error : null;
  if (corpo && typeof corpo === 'object') {
    const titulo = (corpo as { titulo?: unknown }).titulo;
    if (typeof titulo === 'string' && titulo.trim()) {
      return titulo.trim();
    }
  }
  return '';
}
